import { useState, useEffect } from 'react';
import { useInventory } from '../hooks/useInventoryOperations';
import { useItems } from '../hooks/useItems';
import { useCategories } from '../hooks/useCategories';
import { inventoryService } from '../services/inventoryService';

const LotsPage = () => {
  const { getLots, loading } = useInventory();
  const { items } = useItems('', 0, 10000); // Load all items for search
  const { categories } = useCategories();
  const [lots, setLots] = useState([]);
  const [allLots, setAllLots] = useState([]); // Store unfiltered lots
  const [itemFilter, setItemFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showZeroPriceOnly, setShowZeroPriceOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    quantity: '',
    unit_price: '',
    date_received: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Filter items for search dropdown
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchLots();
  }, [itemFilter, includeEmpty]);

  useEffect(() => {
    applyFilters();
  }, [allLots, categoryFilter, showZeroPriceOnly]);

  const fetchLots = async () => {
    try {
      const params = {};
      if (itemFilter) params.item_id = parseInt(itemFilter);
      params.include_empty = includeEmpty;
      
      const data = await getLots(params);
      setAllLots(data);
    } catch (err) {
      console.error('Failed to fetch lots:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...allLots];

    // Filter by category
    if (categoryFilter) {
      filtered = filtered.filter(lot => lot.item?.category?.name === categoryFilter);
    }

    // Filter by zero price
    if (showZeroPriceOnly) {
      filtered = filtered.filter(lot => parseFloat(lot.unit_price) === 0);
    }

    setLots(filtered);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (lot) => {
    if (lot.remaining_quantity === 0) {
      return <span className="badge bg-secondary">Depleted</span>;
    } else if (lot.remaining_quantity < lot.quantity * 0.25) {
      return <span className="badge bg-warning">Low Stock</span>;
    } else {
      return <span className="badge bg-success">Available</span>;
    }
  };

  const handleItemSelect = (item) => {
    setItemFilter(item.id);
    setSearchTerm(item.name);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    if (e.target.value === '') {
      setItemFilter('');
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setItemFilter('');
    setShowDropdown(false);
  };

  const handleEditClick = (lot) => {
    setEditingLot(lot);
    setEditForm({
      quantity: lot.quantity,
      unit_price: lot.unit_price,
      date_received: lot.date_received,
      notes: lot.notes || ''
    });
    setError(null);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await inventoryService.updateLot(editingLot.id, {
        quantity: parseInt(editForm.quantity),
        unit_price: parseFloat(editForm.unit_price),
        date_received: editForm.date_received,
        notes: editForm.notes
      });

      setShowEditModal(false);
      setEditingLot(null);
      await fetchLots();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update lot');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingLot(null);
    setError(null);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Inventory Lots</h2>
        <button className="btn btn-outline-secondary" onClick={fetchLots}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label small">Filter by Item</label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-1"
                    onClick={handleClearSearch}
                    style={{ border: 'none', background: 'transparent' }}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                )}
                {showDropdown && filteredItems.length > 0 && (
                  <div className="dropdown-menu show w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {filteredItems.slice(0, 50).map(item => (
                      <button
                        key={item.id}
                        className="dropdown-item"
                        onClick={() => handleItemSelect(item)}
                      >
                        <div>
                          <strong>{item.name}</strong>
                          <br />
                          <small className="text-muted">{item.category?.name}</small>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label small">Filter by Category</label>
              <select
                className="form-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 d-flex align-items-end mb-3">
              <div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="includeEmpty"
                    checked={includeEmpty}
                    onChange={(e) => setIncludeEmpty(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="includeEmpty">
                    Show depleted lots
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showZeroPrice"
                    checked={showZeroPriceOnly}
                    onChange={(e) => setShowZeroPriceOnly(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showZeroPrice">
                    Show only $0 unit price
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Lot #</th>
                    <th>Item</th>
                    <th>Category</th>
                    <th>Date Received</th>
                    <th className="text-end">Original Qty</th>
                    <th className="text-end">Remaining</th>
                    <th className="text-end">Unit Price</th>
                    <th className="text-end">Lot Value</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center text-muted py-4">
                        No lots found
                      </td>
                    </tr>
                  ) : (
                    lots.map(lot => (
                      <tr key={lot.id}>
                        <td><strong>#{lot.id}</strong></td>
                        <td>{lot.item?.name}</td>
                        <td>
                          <span className="badge bg-info">{lot.item?.category?.name}</span>
                        </td>
                        <td>{formatDate(lot.date_received)}</td>
                        <td className="text-end">{lot.quantity}</td>
                        <td className="text-end">
                          <strong className={lot.remaining_quantity === 0 ? 'text-muted' : ''}>
                            {lot.remaining_quantity}
                          </strong>
                        </td>
                        <td className="text-end">{formatCurrency(lot.unit_price)}</td>
                        <td className="text-end">
                          <strong>{formatCurrency(lot.remaining_quantity * lot.unit_price)}</strong>
                        </td>
                        <td>{getStatusBadge(lot)}</td>
                        <td>
                          <small className="text-muted">{lot.notes || '-'}</small>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditClick(lot)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="table-light">
                    <td colSpan="7" className="text-end"><strong>Total Value:</strong></td>
                    <td className="text-end">
                      <strong>
                        {formatCurrency(
                          lots.reduce((sum, lot) => sum + (lot.remaining_quantity * lot.unit_price), 0)
                        )}
                      </strong>
                    </td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Lot #{editingLot?.id}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  disabled={saving}
                ></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label small text-muted">Item</label>
                    <p className="fw-bold">{editingLot?.item?.name}</p>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Quantity <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={editForm.quantity}
                        onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                        min="1"
                        step="1"
                        required
                      />
                      <small className="text-muted">
                        Current remaining: {editingLot?.remaining_quantity}
                      </small>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Unit Price <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={editForm.unit_price}
                        onChange={(e) => setEditForm({ ...editForm, unit_price: e.target.value })}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Date Received <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={editForm.date_received}
                      onChange={(e) => setEditForm({ ...editForm, date_received: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LotsPage;
